import { useState, useEffect, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';

export type ActiveUsersHistoryRange = 'day' | 'week' | 'month';

export interface ActiveUsersHistoryPoint {
  ts: number; // epoch seconds (bucket start for week/month, sample time for day)
  total: number;
  consumer: number;
  business: number;
}

export interface ActiveUsersHistoryPeak {
  ts: number; // epoch seconds
  total: number;
}

export interface ActiveUsersHistory {
  range: ActiveUsersHistoryRange;
  /** Bucket size for the points (1m=60, 5m=300, 1h=3600). */
  sampleIntervalSeconds: number;
  points: ActiveUsersHistoryPoint[];
  peak: ActiveUsersHistoryPeak | null;
}

const POLL_INTERVAL_MS = 60_000;

/**
 * Fetch the active-users history series from the admin API.
 *
 * Refetches on `range` change and at a steady poll cadence so the chart
 * stays close to live during an event without thrashing the API.
 */
export const useActiveUsersHistory = (
  range: ActiveUsersHistoryRange,
): { data: ActiveUsersHistory | null; loading: boolean; error: string | null } => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [data, setData] = useState<ActiveUsersHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchHistory = async (): Promise<void> => {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        if (isMounted) {
          setError('REACT_APP_API_URL environment variable is not set');
          setLoading(false);
        }
        return;
      }

      try {
        let token: string | null = null;
        const currentAccounts = accountsRef.current;
        if (currentAccounts.length > 0) {
          token = await acquireApiToken(instance, currentAccounts[0]);
        }

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(
          `${apiUrl}/admin/active-users/history?range=${range}`,
          { method: 'GET', headers },
        );
        const body = await response.json();

        if (!response.ok) {
          throw new Error(body.error || `HTTP ${response.status}`);
        }
        if (!body.success || !body.data) {
          throw new Error(body.error || 'Failed to fetch active-users history');
        }

        if (isMounted) {
          setData(body.data as ActiveUsersHistory);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message ?? 'Unknown error');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    setLoading(true);
    const tick = async (): Promise<void> => {
      await fetchHistory();
      if (isMounted) {
        timer = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    tick();

    return () => {
      isMounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [isAuthenticated, instance, range]);

  return { data, loading, error };
};
